/* eslint-disable react-hooks/rules-of-hooks */
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { useFieldValue, useSetFieldValue } from "formik";
import { max } from "lodash";
import { useEffect } from "react";
import { useClearEnrollingConditionSet } from "../hooks/useClearEnrollingConditionSet";
import { useScopes } from "../hooks/useScopes";
import { FlexContext } from "../models/flexible/flex-context";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { FlexSectionSchema } from "../models/flexible/flex-section-schema";
import { TypedFlexEntity } from "../models/flexible/typed-flex-entity";
import { combinePaths } from "../models/flexible/utils";
import { ConditionLogicalOperator, FlexFormStatus, Preparation } from "../models/generated/graphql";
import { castToArray, getPreparationsIfExist, returnFirstResult } from "../utils/agreement-utils";

function PreparationsFillingFromCaseEffect(props: { section: FlexSectionController }) {
	const preparationsValue: Array<{ [AttributeIdentifiers.BrandName]: Preparation }> = castToArray(
		useFieldValue(props.section.path)
	).filter((sectionValue) => sectionValue[AttributeIdentifiers.BrandName]);
	const setFieldValue = useSetFieldValue();

	if (!props.section.context.cases.length || !props.section.context.isEditable || props.section.context.isNegotiable) {
		return null;
	}

	useEffect(() => {
		const caseInstance = props.section.context.cases[0]!;
		const preparations = returnFirstResult(caseInstance.flexEntities, (entity: TypedFlexEntity<any>) =>
			getPreparationsIfExist(entity.data)
		);
		if (
			!preparations?.length ||
			preparationsValue.filter((values) => !!values.brandName).length ||
			props.section.context.flexEntity?.status === FlexFormStatus.Submitted
		) {
			return;
		}

		const isBrandNameField = props.section.fieldControllers.some(
			(field) => field.schema.attribute.identifier === AttributeIdentifiers.BrandName
		);
		const isGenericNameField = props.section.fieldControllers.some(
			(field) => field.schema.attribute.identifier === AttributeIdentifiers.GenericName
		);
		const isNotesField = props.section.fieldControllers.some(
			(field) => field.schema.attribute.identifier === AttributeIdentifiers.Notes
		);

		if (!props.section.schema.isArray) {
			if (isBrandNameField) {
				setFieldValue(combinePaths(props.section.path, AttributeIdentifiers.BrandName), [preparations[0].brandName]);
			}
			if (isGenericNameField) {
				setFieldValue(combinePaths(props.section.path, AttributeIdentifiers.GenericName), preparations[0].genericName);
			}
			if (isNotesField) {
				setFieldValue(combinePaths(props.section.path, AttributeIdentifiers.Notes), preparations[0].notes);
			}
			return;
		}
		setFieldValue(
			props.section.path,
			preparations.map((preparation) => ({
				...(isBrandNameField ? { [AttributeIdentifiers.BrandName]: preparation.brandName } : []),
				...(isGenericNameField ? { [AttributeIdentifiers.GenericName]: preparation.genericName } : {}),
				...(isNotesField ? { [AttributeIdentifiers.Notes]: preparation.notes } : {}),
			}))
		);
	}, []);

	return null;
}

export class PreparationSectionController implements FlexSectionController {
	context: FlexContext = {} as any;

	constructor(
		readonly schema: FlexSectionSchema,
		readonly path: string,
		readonly fieldControllers: Array<FlexFieldController>,
		readonly sectionControllers: Array<FlexSectionController>
	) {}

	useCanAddItem(): boolean {
		if (this.context.isNegotiable) {
			return false;
		}

		const [preparations] = useFieldValue(this.path);
		const { selectedScopes } = useScopes(this.context);
		const maximumTreatmentSetLength =
			max(
				selectedScopes
					.filter((agreementScope) => agreementScope.logicalOperator === ConditionLogicalOperator.And)
					.map((agreementScope) => agreementScope.treatmentSets.length)
			) || 0;

		return !this.context.cases?.length && !!preparations.length && maximumTreatmentSetLength > preparations.length;
	}

	useCanDeleteItem(): boolean {
		return !this.context.cases?.length;
	}

	useOnRowDeleted() {
		return useClearEnrollingConditionSet(this.context);
	}

	renderBeforeHeader(): React.ReactElement {
		return this.context.flexEntity ? <></> : <PreparationsFillingFromCaseEffect section={this} />;
	}
}
